import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Pencil, Trash2, Search, UserRound, HeartPulse } from 'lucide-react';
import api from '@/lib/api';

export default function AdminAlumnos() {
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        first_name: '', last_name: '', dni: '', phone: '', email: '',
        birth_date: '', weight: '', height: '', notes: '', goals: '', medical_observations: '',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/customers', { params: { search, page, per_page: 10 } });
            setCustomers(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, page]);

    useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

    const openEdit = async (customer) => {
        setEditing(customer);
        setForm({
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            dni: customer.dni || '',
            phone: customer.phone || '',
            email: customer.email || '',
            birth_date: customer.birth_date ? customer.birth_date.split('T')[0] : '',
            weight: customer.weight ?? '',
            height: customer.height ?? '',
            notes: customer.notes || '',
            goals: customer.goals || '',
            medical_observations: customer.medical_observations || '',
        });
        setError('');
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = { ...form };
            if (payload.weight === '') payload.weight = null;
            if (payload.height === '') payload.height = null;
            if (payload.birth_date === '') payload.birth_date = null;
            await api.put(`/admin/customers/${editing.id}`, payload);
            setDialogOpen(false);
            fetchCustomers();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este alumno?')) return;
        try {
            await api.delete(`/admin/customers/${id}`);
            fetchCustomers();
        } catch {}
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Alumnos</h1>
            </div>

            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input
                            placeholder="Buscar alumnos..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="max-w-sm"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : customers.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron alumnos.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[600px]">
                                    <thead>
                                        <tr className="border-b border-hairline-dark">
                                            <th className="text-left py-3 px-2 font-medium">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium">DNI</th>
                                            <th className="text-left py-3 px-2 font-medium">Gimnasio</th>
                                            <th className="text-left py-3 px-2 font-medium">Teléfono</th>
                                            <th className="text-left py-3 px-2 font-medium">Peso</th>
                                            <th className="text-left py-3 px-2 font-medium">Altura</th>
                                            <th className="text-right py-3 px-2 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customers.map(c => (
                                            <tr key={c.id} className="border-b border-hairline-dark last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2">
                                                    <div className="flex items-center gap-2">
                                                        <UserRound className="h-4 w-4 text-white/40" />
                                                        <span className="font-medium">{c.first_name} {c.last_name}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-2">{c.dni}</td>
                                                <td className="py-3 px-2">{c.gym?.name || '-'}</td>
                                                <td className="py-3 px-2">{c.phone || '-'}</td>
                                                <td className="py-3 px-2">{c.weight ? `${c.weight} kg` : '-'}</td>
                                                <td className="py-3 px-2">{c.height ? `${c.height} cm` : '-'}</td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="outline" size="icon" onClick={() => navigate(`/admin/mediciones?customer=${c.id}`)} title="Mediciones">
                                                            <HeartPulse className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(c)}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="outline" size="icon" onClick={() => handleDelete(c.id)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Editar Alumno</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-[#0a0a0a] border border-hairline-dark text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Nombre</Label>
                                <Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Apellido</Label>
                                <Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>DNI</Label>
                                <Input value={form.dni} onChange={e => setForm({ ...form, dni: e.target.value })} required />
                            </div>
                            <div className="space-y-2">
                                <Label>Fecha de Nacimiento</Label>
                                <Input type="date" value={form.birth_date} onChange={e => setForm({ ...form, birth_date: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Peso (kg)</Label>
                                <Input type="number" step="0.1" min="0" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Altura (cm)</Label>
                                <Input type="number" step="0.1" min="0" value={form.height} onChange={e => setForm({ ...form, height: e.target.value })} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Objetivos</Label>
                            <Textarea value={form.goals} onChange={e => setForm({ ...form, goals: e.target.value })} rows={2} />
                        </div>
                        <div className="space-y-2">
                            <Label>Observaciones Médicas</Label>
                            <Textarea value={form.medical_observations} onChange={e => setForm({ ...form, medical_observations: e.target.value })} rows={2} />
                        </div>
                        <div className="space-y-2">
                            <Label>Notas</Label>
                            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
